import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGlobalContext } from "../../context/global";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FileDownloadOutlined } from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import Chart from "../../components/Reports/Chart";
import cubejs from "@cubejs-client/core";
import useToken from "../../helper/Reports/useToken";
import { toast } from "react-toastify";
import { getClientDataFromAdyogiAPI } from "../../helper/clientData/utils";
import { getTodayCalendarDate } from "../../constant/calendarDefaultConfigurations";
import Header from "../../components/header/header";
import ExcelJS from "exceljs";
import MarketingStreamTable from "./MarketingStreamTable";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import { masterApi } from "../../apis/masterApi";
import { encodeData } from "../../helper/common/encodeData";
import { getCurrencySymbol } from "../../constant/defaultCurrencyConfigurations";
import {
  ColumnCustomizationButton,
  COLUMN_CUSTOMIZATION_PAGE_IDS,
  V2_STANDARD_VIEW_OPTIONS,
  usePageColumnCustomization,
} from "../../components/ColumnCustomization";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 300, 500];
const CAMPAIGN_DROPDOWN_LIMIT = 200;
const CAMPAIGN_TAG_LIMIT = 1;

const HOUR_DIMENSION = "ams_sp_traffic.hour";
const DATE_DIMENSION = "ams_sp_traffic.date";
const TIME_DIMENSION = "ams_sp_traffic.time_window_start";
const CAMPAIGN_NAME_DIMENSION = "ams_sp_traffic.campaign_name";

const ANALYSIS_BY_OPTIONS = [
  {
    label: "Hour",
    value: "hour",
    dimensions: [HOUR_DIMENSION],
    order: { [HOUR_DIMENSION]: "asc" },
  },
  {
    label: "Date",
    value: "date",
    dimensions: [DATE_DIMENSION],
    order: { [DATE_DIMENSION]: "asc" },
  },
  {
    label: "Date + Hour",
    value: "date_hour",
    dimensions: [DATE_DIMENSION, HOUR_DIMENSION],
    order: [
      [DATE_DIMENSION, "asc"],
      [HOUR_DIMENSION, "asc"],
    ],
  },
];

const EXTRA_GROUP_BY_OPTIONS = [
  {
    label: "Campaign Name",
    value: "campaign_name",
    dimensions: [CAMPAIGN_NAME_DIMENSION],
  },
];

const CUBE_MEASURES = [
  "ams_sp_traffic.cost",
  "ams_sp_traffic.sales14d",
  "ams_sp_traffic.roas",
  "ams_sp_traffic.acos",
  "ams_sp_traffic.impressions",
  "ams_sp_traffic.clicks",
  "ams_sp_traffic.conversions14d",
  "ams_sp_traffic.TotalOrderItemCount",
  "ams_sp_traffic.average_order_value",
  "ams_sp_traffic.cpc",
  "ams_sp_traffic.cpm",
  "ams_sp_traffic.ctr",
  "ams_sp_traffic.conversion_rate",
];

const MEASURE_META = {
  "ams_sp_traffic.cost": {
    shortTitle: "Spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.sales14d": {
    shortTitle: "Revenue",
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.roas": {
    shortTitle: "ROAS",
    isDerivedValue: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.acos": {
    shortTitle: "ACOS",
    isDerivedValue: true,
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  "ams_sp_traffic.impressions": {
    shortTitle: "Impressions",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.clicks": {
    shortTitle: "Clicks",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.conversions14d": {
    shortTitle: "Orders",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.TotalOrderItemCount": {
    shortTitle: "Order Items",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.average_order_value": {
    shortTitle: "AOV",
    isDerivedValue: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  "ams_sp_traffic.cpc": {
    shortTitle: "CPC",
    isDerivedValue: true,
    isCurrencyFormat: true,
    maximumFractionDigits: 3,
    viewType: ["PERFORMANCE"],
  },
  "ams_sp_traffic.cpm": {
    shortTitle: "CPM",
    isDerivedValue: true,
    isCurrencyFormat: true,
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE"],
  },
  "ams_sp_traffic.ctr": {
    shortTitle: "CTR",
    isDerivedValue: true,
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["PERFORMANCE", "FUNNEL"],
  },
  "ams_sp_traffic.conversion_rate": {
    shortTitle: "Conversion Rate",
    isDerivedValue: true,
    format: "percent",
    maximumFractionDigits: 2,
    viewType: ["FUNNEL"],
  },
};

const SUM_MEASURES = [
  "ams_sp_traffic.cost",
  "ams_sp_traffic.sales14d",
  "ams_sp_traffic.impressions",
  "ams_sp_traffic.clicks",
  "ams_sp_traffic.conversions14d",
  "ams_sp_traffic.TotalOrderItemCount",
];

const formatDate = (date) => {
  const offset = date.getTimezoneOffset();
  const adjusted = new Date(date.getTime() - offset * 60 * 1000);
  return adjusted.toISOString().split("T")[0].toString();
};

const formatCubeDateToDdMmYyyy = (value) => {
  if (value == null || value === "") return value;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[3]}-${match[2]}-${match[1]}`;
};

/** 19:00 → 19:00 - 20:00, 23:00 → 23:00 - 00:00 */
const formatHourToRange = (value) => {
  if (value == null || value === "") return value;
  const raw = String(value).trim();
  // Already formatted as a range
  if (raw.includes(" - ")) return raw;

  const match = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return value;
  const hour = Number(match[1]);
  const minutes = match[2];
  if (Number.isNaN(hour) || hour < 0 || hour > 23) return value;
  const nextHour = (hour + 1) % 24;
  const start = `${String(hour).padStart(2, "0")}:${minutes}`;
  const end = `${String(nextHour).padStart(2, "0")}:${minutes}`;
  return `${start} - ${end}`;
};

const formatTableRowDisplayValues = (row = {}) => {
  const nextRow = { ...row };
  if (nextRow[DATE_DIMENSION] != null) {
    nextRow[DATE_DIMENSION] = formatCubeDateToDdMmYyyy(nextRow[DATE_DIMENSION]);
  }
  if (nextRow[HOUR_DIMENSION] != null) {
    nextRow[HOUR_DIMENSION] = formatHourToRange(nextRow[HOUR_DIMENSION]);
  }
  return nextRow;
};

const isDateDimensionKey = (key) => key === DATE_DIMENSION;

const cloneCubeResultSet = (resultSet) => {
  if (!resultSet?.loadResponse) return resultSet;
  const clone = Object.create(Object.getPrototypeOf(resultSet));
  Object.assign(clone, resultSet);
  clone.loadResponse = JSON.parse(JSON.stringify(resultSet.loadResponse));
  return clone;
};

/** Format Date (DD-MM-YYYY) and Hour (range) for table display */
const formatTableResultSet = (resultSet) => {
  if (!resultSet?.loadResponse?.results?.[0]) return resultSet;

  const cloned = cloneCubeResultSet(resultSet);
  const result = cloned.loadResponse.results[0];
  const annotation = result.annotation || {};

  if (annotation.dimensions?.[DATE_DIMENSION]) {
    annotation.dimensions[DATE_DIMENSION] = {
      ...annotation.dimensions[DATE_DIMENSION],
      title: "Date",
      shortTitle: "Date",
    };
  }
  if (annotation.dimensions?.[HOUR_DIMENSION]) {
    annotation.dimensions[HOUR_DIMENSION] = {
      ...annotation.dimensions[HOUR_DIMENSION],
      title: "Hour",
      shortTitle: "Hour",
    };
  }

  result.data = (result.data || []).map(formatTableRowDisplayValues);

  return cloned;
};

const getDateRangeKey = (range) => {
  if (!range?.[0]?.startDate || !range?.[0]?.endDate) return "";
  return `${formatDate(range[0].startDate)}_${formatDate(range[0].endDate)}`;
};

const safeNumber = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : 0;
};

const computeSummary = (rows = []) => {
  const summary = {};
  SUM_MEASURES.forEach((key) => {
    summary[key] = rows.reduce((acc, row) => acc + safeNumber(row[key]), 0);
  });

  const cost = summary["ams_sp_traffic.cost"];
  const sales = summary["ams_sp_traffic.sales14d"];
  const clicks = summary["ams_sp_traffic.clicks"];
  const impressions = summary["ams_sp_traffic.impressions"];
  const orders = summary["ams_sp_traffic.conversions14d"];

  summary["ams_sp_traffic.roas"] = cost ? Number((sales / cost).toFixed(2)) : 0;
  summary["ams_sp_traffic.acos"] = sales
    ? Number(((cost / sales) * 100).toFixed(2))
    : 0;
  summary["ams_sp_traffic.cpc"] = clicks
    ? Number((cost / clicks).toFixed(3))
    : 0;
  summary["ams_sp_traffic.cpm"] = impressions
    ? Number(((cost * 1000) / impressions).toFixed(2))
    : 0;
  summary["ams_sp_traffic.ctr"] = impressions
    ? Number(((clicks / impressions) * 100).toFixed(2))
    : 0;
  summary["ams_sp_traffic.conversion_rate"] = clicks
    ? Number(((orders / clicks) * 100).toFixed(2))
    : 0;
  summary["ams_sp_traffic.average_order_value"] =
    sales >= 1 && orders >= 1 ? Number((sales / orders).toFixed(0)) : 0;

  return summary;
};

const DIMENSION_HEAD_META = {
  [DATE_DIMENSION]: {
    shortTitle: "Date",
    minWidth: 120,
    isRemovable: false,
  },
  [HOUR_DIMENSION]: {
    shortTitle: "Hour",
    minWidth: 140,
    isRemovable: false,
  },
  [CAMPAIGN_NAME_DIMENSION]: {
    shortTitle: "Campaign Name",
    minWidth: 200,
    isRemovable: true,
  },
};

const buildDataHeads = (dimensionKeys = [], cubeColumns = []) => {
  let stickyLeft = 0;
  const dimensionHeads = dimensionKeys.map((key) => {
    const meta = DIMENSION_HEAD_META[key] || {
      shortTitle: key.split(".").pop(),
      minWidth: 120,
      isRemovable: false,
    };
    const head = {
      key,
      shortTitle: meta.shortTitle,
      isSticky: true,
      isDimension: true,
      isRemovable: !!meta.isRemovable,
      stickyLeft,
      minWidth: meta.minWidth,
      alignment: "left",
    };
    stickyLeft += meta.minWidth;
    return head;
  });

  const measureHeads = CUBE_MEASURES.map((measureKey) => {
    const cubeCol = cubeColumns.find((col) => col.key === measureKey);
    const meta = MEASURE_META[measureKey] || {};
    return {
      key: measureKey,
      shortTitle:
        meta.shortTitle || cubeCol?.shortTitle || measureKey.split(".").pop(),
      isSticky: false,
      isDimension: false,
      alignment: "right",
      minWidth: 120,
      isCurrencyFormat: !!meta.isCurrencyFormat,
      isDerivedValue: !!meta.isDerivedValue,
      format: meta.format || "number",
      maximumFractionDigits: meta.maximumFractionDigits ?? 2,
      viewType: meta.viewType || ["PERFORMANCE", "FUNNEL"],
    };
  });

  return [...dimensionHeads, ...measureHeads];
};

const buildTableFromResultSet = (resultSet, dimensionKeys = []) => {
  let columns = [];
  let rows = [];

  if (resultSet && typeof resultSet.tableColumns === "function") {
    try {
      columns = resultSet.tableColumns({}) || [];
      rows = resultSet.tablePivot({}) || [];
    } catch (_error) {
      columns = [];
      rows = [];
    }

    // Fallback to raw cube rows if pivot is empty
    if (!rows.length && resultSet.loadResponse?.results?.[0]?.data) {
      rows = resultSet.loadResponse.results[0].data;
    }
  }

  const resolvedDimensionKeys =
    dimensionKeys.length > 0
      ? dimensionKeys
      : [
          DATE_DIMENSION,
          HOUR_DIMENSION,
          CAMPAIGN_NAME_DIMENSION,
        ].filter((key) =>
          rows[0] ? Object.prototype.hasOwnProperty.call(rows[0], key) : false
        );

  return {
    dataHeads: buildDataHeads(resolvedDimensionKeys, columns),
    dataBody: rows.map(formatTableRowDisplayValues),
  };
};

const getTableOrder = (sortKey, direction, analysisValue) => {
  if (!sortKey) {
    return { [HOUR_DIMENSION]: "asc" };
  }
  // Keep date+hour rows stable when sorting by date
  if (analysisValue === "date_hour" && sortKey === DATE_DIMENSION) {
    return [
      [DATE_DIMENSION, direction],
      [HOUR_DIMENSION, direction],
    ];
  }
  return [[sortKey, direction]];
};

const mapCampaignListOption = (item) => ({
  id: item?.id,
  name: item?.name || "",
  platform_id: item?.platform_id != null ? String(item.platform_id) : "",
  status: item?.status,
  label: item?.name || "",
  value:
    item?.platform_id != null
      ? String(item.platform_id)
      : String(item?.id || item?.name || ""),
});

function CampaignStatusDot({ status }) {
  const normalized = String(status ?? "").toUpperCase();
  if (!normalized) return null;
  const isEnabled = normalized === "ENABLED";
  return (
    <Box
      title={normalized}
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        flexShrink: 0,
        bgcolor: isEnabled ? "#12B76A" : "#98A2B3",
        alignSelf: "center",
      }}
    />
  );
}

const fetchMarketingStreamCampaigns = async (searchTerm = "") => {
  const reqData = {
    source: "amazon",
    fields: "id,name,platform_id,status",
    limit: CAMPAIGN_DROPDOWN_LIMIT,
    order_by: "daily_budget",
    sorting_order: "DESC",
  };
  const term = String(searchTerm || "").trim();
  if (term) {
    reqData.filtering = encodeData([
      {
        field_name: "name",
        operator: "CONTAINS",
        value: term,
      },
    ]);
  }

  const result = await masterApi(reqData, "campaignList", false);
  return (result?.data?.data || [])
    .map(mapCampaignListOption)
    .filter((item) => item.name && (item.platform_id || item.id));
};

const renderCampaignTags = (value, getTagProps) => {
  const visible = value.slice(0, CAMPAIGN_TAG_LIMIT);
  const hiddenCount = value.length - visible.length;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 0.5,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      {visible.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });
        return (
          <Chip
            key={key}
            size="small"
            label={option?.label || option?.name || ""}
            title={option?.label || option?.name || ""}
            {...tagProps}
            sx={{
              maxWidth: 160,
              fontSize: "11px",
              height: 22,
              flexShrink: 1,
              minWidth: 0,
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                px: 1,
              },
            }}
          />
        );
      })}
      {hiddenCount > 0 ? (
        <Chip
          size="small"
          label={`+${hiddenCount}`}
          sx={{
            fontWeight: 700,
            fontSize: "11px",
            height: 22,
            flexShrink: 0,
            bgcolor: "#F3EFFF",
            color: "#6B4EFF",
          }}
        />
      ) : null}
    </Box>
  );
};

const analysisByAutocompleteSx = {
  minWidth: "180px",
  "& .MuiAutocomplete-inputRoot": {
    fontSize: "12px",
    color: "#333333",
  },
  "& .MuiAutocomplete-input": {
    fontSize: "12px",
    color: "#333333",
  },
  "& .MuiSvgIcon-root": {
    color: "#333333",
  },
};

const MarketingStream = () => {
  const dashboardSideBarVisibiltyState = useSelector(
    (state) => state.dashboardData.dashboardSideBarVisibiltyState
  );
  const [, refresh] = useToken("");
  const { cubeToken, setCubeToken, clientId } = useGlobalContext();

  const [calendarDate, setCalendarDate] = useState(() => getTodayCalendarDate());
  const [applyCalendar, setApplyCalendar] = useState(1);

  const [entityID, setEntityID] = useState(false);
  const [analysisBy, setAnalysisBy] = useState("hour");
  const [extraDimensions, setExtraDimensions] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [appliedCampaignNames, setAppliedCampaignNames] = useState([]);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [campaignInput, setCampaignInput] = useState("");
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const [isLoaderVisible, setIsLoaderVisible] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationLimit, setPaginationLimit] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [resultSet, setResultSet] = useState({});
  const [chartResultSet, setChartResultSet] = useState({});
  const [resultSetComparison, setResultSetComparison] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: HOUR_DIMENSION,
    direction: "asc",
  });
  const [isDownloading, setIsDownloading] = useState(false);

  const refreshRef = useRef(refresh);
  const setCubeTokenRef = useRef(setCubeToken);
  refreshRef.current = refresh;
  setCubeTokenRef.current = setCubeToken;

  const selectedAnalysisOption = useMemo(
    () =>
      ANALYSIS_BY_OPTIONS.find((option) => option.value === analysisBy) ||
      ANALYSIS_BY_OPTIONS[0],
    [analysisBy]
  );

  const startDate = formatDate(calendarDate[0].startDate);
  const endDate = formatDate(calendarDate[0].endDate);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getClientDataFromAdyogiAPI(clientId);
      setEntityID(
        response.data.results[0].amazon_setup.profile_info.accountInfo.id
      );
    };
    if (clientId) {
      fetchData();
    }
  }, [clientId]);

  const handleLocalCalendarChange = useCallback(
    ({ calendarDate: nextCalendarDate, applyCalendar: nextApplyCalendar }) => {
      if (nextCalendarDate) {
        setCalendarDate((prev) =>
          getDateRangeKey(prev) === getDateRangeKey(nextCalendarDate)
            ? prev
            : nextCalendarDate
        );
      }
      if (typeof nextApplyCalendar === "number") {
        setIsLoaderVisible(true);
        setApplyCalendar((prev) =>
          prev === nextApplyCalendar ? prev : nextApplyCalendar
        );
      }
    },
    []
  );

  useEffect(() => {
    setSortConfig({
      key: analysisBy === "hour" ? HOUR_DIMENSION : DATE_DIMENSION,
      direction: "asc",
    });
  }, [analysisBy]);

  // Reset to first page when filters / analysis / date range change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    analysisBy,
    applyCalendar,
    appliedCampaignNames,
    extraDimensions,
    startDate,
    endDate,
  ]);

  // Campaign dropdown options (Ads campaignList — no date range)
  useEffect(() => {
    if (!clientId) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsLoadingCampaigns(true);
      try {
        const rows = await fetchMarketingStreamCampaigns(campaignInput);
        if (!cancelled) setCampaignOptions(rows);
      } catch (error) {
        console.error(error);
        if (!cancelled) setCampaignOptions([]);
      } finally {
        if (!cancelled) setIsLoadingCampaigns(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clientId, campaignInput]);

  const buildSharedFilters = useCallback(() => {
    const filters = [
      {
        member: "ams_sp_traffic.advertiser_id",
        operator: "equals",
        values: [entityID],
      },
    ];
    if (appliedCampaignNames.length > 0) {
      filters.push({
        member: CAMPAIGN_NAME_DIMENSION,
        operator: "equals",
        values: appliedCampaignNames,
      });
    }
    return filters;
  }, [appliedCampaignNames, entityID]);

  const hasPendingCampaignFilterChange = useMemo(() => {
    const selectedNames = selectedCampaigns
      .map((campaign) => campaign?.name || "")
      .filter(Boolean)
      .sort();
    const appliedNames = [...appliedCampaignNames].sort();
    if (selectedNames.length !== appliedNames.length) return true;
    return selectedNames.some((name, index) => name !== appliedNames[index]);
  }, [appliedCampaignNames, selectedCampaigns]);

  const mergedCampaignOptions = useMemo(() => {
    const optionsByValue = new Map();
    [...campaignOptions, ...selectedCampaigns].forEach((option) => {
      if (option?.value) {
        optionsByValue.set(option.value, option);
      }
    });
    return Array.from(optionsByValue.values());
  }, [campaignOptions, selectedCampaigns]);

  const handleApplyCampaignFilter = useCallback(() => {
    const nextNames = selectedCampaigns
      .map((campaign) => campaign?.name || "")
      .filter(Boolean);
    setIsLoaderVisible(true);
    setCurrentPage(1);
    setAppliedCampaignNames(nextNames);
  }, [selectedCampaigns]);

  // Charts — unpaginated; do not refetch on page change
  useEffect(() => {
    if (!cubeToken || !entityID) {
      return;
    }

    let cancelled = false;
    const cubejsApi = cubejs(cubeToken, {
      apiUrl: process.env.REACT_APP_CUBE_JS_URL,
    });
    const dateRange = [startDate, endDate];
    const today = formatDate(new Date());
    const todayRange = [today, today];
    const filters = buildSharedFilters();

    const buildTimeDimensions = (range) => [
      {
        dimension: TIME_DIMENSION,
        dateRange: range,
      },
    ];

    const hourQuery = {
      measures: CUBE_MEASURES,
      order: { [HOUR_DIMENSION]: "asc" },
      dimensions: [HOUR_DIMENSION],
      filters,
      timeDimensions: buildTimeDimensions(dateRange),
      timezone: "Asia/Kolkata",
      limit: 500,
    };

    const comparisonQuery = {
      measures: CUBE_MEASURES,
      order: { [HOUR_DIMENSION]: "asc" },
      dimensions: [HOUR_DIMENSION],
      filters,
      timeDimensions: buildTimeDimensions(todayRange),
      timezone: "Asia/Kolkata",
      limit: 500,
    };

    const loadOptions = {
      headers: {
        "Access-Control-Allow-Origin": true,
      },
    };

    Promise.all([
      cubejsApi.load(comparisonQuery, loadOptions),
      cubejsApi.load(hourQuery, loadOptions),
    ])
      .then(([comparisonResults, hourResults]) => {
        if (cancelled) return;
        setResultSetComparison(comparisonResults);
        setChartResultSet(hourResults);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error?.response?.error || error);
        if (error?.message === "Invalid token") {
          setCubeTokenRef.current(false);
          localStorage.setItem("cubeToken", null);
          refreshRef.current();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    applyCalendar,
    buildSharedFilters,
    cubeToken,
    endDate,
    entityID,
    startDate,
  ]);

  // Table — server-side pagination
  useEffect(() => {
    if (!cubeToken || !entityID) {
      return;
    }

    let cancelled = false;
    setIsLoaderVisible(true);

    const cubejsApi = cubejs(cubeToken, {
      apiUrl: process.env.REACT_APP_CUBE_JS_URL,
    });
    const dateRange = [startDate, endDate];
    const filters = buildSharedFilters();
    const analysisOption =
      ANALYSIS_BY_OPTIONS.find((option) => option.value === analysisBy) ||
      ANALYSIS_BY_OPTIONS[0];
    const tableDimensions = [
      ...analysisOption.dimensions,
      ...extraDimensions.filter(
        (dimension) => !analysisOption.dimensions.includes(dimension)
      ),
    ];

    const tableQuery = {
      measures: CUBE_MEASURES,
      order: getTableOrder(sortConfig.key, sortConfig.direction, analysisBy),
      dimensions: tableDimensions,
      filters,
      timeDimensions: [
        {
          dimension: TIME_DIMENSION,
          dateRange,
        },
      ],
      timezone: "Asia/Kolkata",
      limit: paginationLimit,
      offset: (currentPage - 1) * paginationLimit,
      total: true,
    };

    const loadOptions = {
      headers: {
        "Access-Control-Allow-Origin": true,
      },
    };

    cubejsApi
      .load(tableQuery, loadOptions)
      .then((tableResults) => {
        if (cancelled) return;
        setResultSet(formatTableResultSet(tableResults));
        setTotalCount(tableResults?.loadResponse?.results?.[0]?.total ?? 0);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error?.response?.error || error);
        if (error?.message === "Invalid token") {
          setCubeTokenRef.current(false);
          localStorage.setItem("cubeToken", null);
          refreshRef.current();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoaderVisible(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    analysisBy,
    applyCalendar,
    buildSharedFilters,
    cubeToken,
    currentPage,
    endDate,
    entityID,
    extraDimensions,
    paginationLimit,
    sortConfig.direction,
    sortConfig.key,
    startDate,
  ]);

  const handlePageChange = (pageNumber) => {
    setIsLoaderVisible(true);
    setCurrentPage(pageNumber);
  };

  const handlePageLimitChange = (event) => {
    setIsLoaderVisible(true);
    setPaginationLimit(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleExtraGroupByChange = useCallback((dimensions = [], action) => {
    setIsLoaderVisible(true);
    setExtraDimensions((prev) => {
      if (action === "add") {
        return [...new Set([...prev, ...dimensions])];
      }
      if (action === "remove") {
        return prev.filter((dimension) => !dimensions.includes(dimension));
      }
      return prev;
    });
  }, []);

  const availableExtraGroupByOptions = useMemo(
    () =>
      EXTRA_GROUP_BY_OPTIONS.filter(
        (option) =>
          !option.dimensions.every((dimension) =>
            extraDimensions.includes(dimension)
          )
      ),
    [extraDimensions]
  );

  const tableDimensions = useMemo(() => {
    const analysisDimensions = selectedAnalysisOption.dimensions || [];
    return [
      ...analysisDimensions,
      ...extraDimensions.filter(
        (dimension) => !analysisDimensions.includes(dimension)
      ),
    ];
  }, [extraDimensions, selectedAnalysisOption]);

  const allColumns = useMemo(
    () => buildDataHeads(tableDimensions, []),
    [tableDimensions]
  );

  const location = useLocation();
  const viewTYPE =
    new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
  const userPlatform =
    JSON.parse(localStorage.getItem("user_platform")) ?? "amazon";

  const {
    currentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving: isColumnCustomizationSaving,
    syncFromViewType,
    handleApply: handleApplyColumnCustomization,
    handleSave: handleSaveColumnCustomization,
    handleSaveAs: handleSaveAsColumnCustomization,
    handleRenameView: handleRenameColumnView,
    handleDeleteView: handleDeleteColumnView,
  } = usePageColumnCustomization({
    pageId: COLUMN_CUSTOMIZATION_PAGE_IDS.MARKETING_STREAM,
    allColumns,
    standardViewOptions: V2_STANDARD_VIEW_OPTIONS,
    initialViewType: viewTYPE,
    platform: userPlatform,
    seedWithAllMetrics: true,
  });

  useEffect(() => {
    const viewtype =
      new URLSearchParams(location.search).get("viewtype") ?? "PERFORMANCE";
    syncFromViewType(viewtype);
  }, [location.search]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set("viewtype", currentViewType);
    const newSearch = queryParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (newSearch === currentSearch) return;
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${newSearch}`
    );
  }, [currentViewType]);

  const totalPages = Math.max(1, Math.ceil(totalCount / paginationLimit));

  const { dataBody } = useMemo(
    () => buildTableFromResultSet(resultSet, tableDimensions),
    [resultSet, tableDimensions]
  );

  const dataHeads = visibleTableColumns;

  const resolvedSortKey = useMemo(() => {
    if (!sortConfig.key) return sortConfig.key;
    if (sortConfig.key === DATE_DIMENSION) {
      const dateHead = dataHeads.find((col) => isDateDimensionKey(col.key));
      return dateHead?.key || sortConfig.key;
    }
    return sortConfig.key;
  }, [dataHeads, sortConfig.key]);

  const summaryData = useMemo(() => computeSummary(dataBody), [dataBody]);

  const handleSortClick = useCallback((property, direction) => {
    setIsLoaderVisible(true);
    setCurrentPage(1);
    setSortConfig({ key: property, direction });
  }, []);

  const handleDownload = useCallback(async () => {
    if (!dataHeads.length || !dataBody.length) {
      toast.info("No data available to download");
      return;
    }
    try {
      setIsDownloading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Marketing Stream");
      const headers = dataHeads.map((col) => col.shortTitle);
      worksheet.addRow(headers);

      const summaryRow = dataHeads.map((col, index) => {
        if (index === 0) return "Summary";
        if (col.isDimension) return "";
        const value = summaryData[col.key];
        return value == null ? "-" : Number(value);
      });
      worksheet.addRow(summaryRow);

      dataBody.forEach((row) => {
        worksheet.addRow(
          dataHeads.map((col) => {
            const value = row[col.key];
            if (col.isDimension) return value ?? "";
            const num = parseFloat(value);
            return Number.isFinite(num) ? num : value ?? "";
          })
        );
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `marketing-stream-${startDate}-to-${endDate}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download report");
    } finally {
      setIsDownloading(false);
    }
  }, [dataBody, dataHeads, endDate, startDate, summaryData]);

  function formatPieChartData(result, type, key, decimals) {
    const formatSeriesValue = (rawValue) => {
      if (decimals == null || rawValue == null || rawValue === "") {
        return rawValue;
      }
      const numericValue = Number(rawValue);
      return Number.isFinite(numericValue)
        ? Number(numericValue.toFixed(decimals))
        : rawValue;
    };

    if (Object.keys(result).length > 0) {
      let cubeResult = JSON.parse(JSON.stringify(result));
      let returnResultLabel = [];
      let returnResultSeries = [];
      let data = cubeResult?.loadResponse?.results[0]?.data;
      data?.forEach((value) => {
        if (value[HOUR_DIMENSION] == null) {
          returnResultLabel.push("NULL");
        } else {
          returnResultLabel.push(formatHourToRange(value[HOUR_DIMENSION]));
        }
        returnResultSeries.push(
          formatSeriesValue(value["ams_sp_traffic." + type])
        );
      });
      let cubeResultLine = JSON.parse(JSON.stringify(resultSetComparison));
      let cubeLineData = cubeResultLine?.loadResponse?.results[0]?.data;
      let returnResultLineLabel = [];
      let returnResultLineSeries = [];
      if (cubeLineData) {
        cubeLineData.forEach((value) => {
          if (value[HOUR_DIMENSION] == null) {
            returnResultLineLabel.push("NULL");
          } else {
            returnResultLineLabel.push(formatHourToRange(value[HOUR_DIMENSION]));
          }
          returnResultLineSeries.push(
            formatSeriesValue(value["ams_sp_traffic." + type])
          );
        });
      }

      return {
        labels: returnResultLabel,
        series: returnResultSeries,
        label: type,
        key: key,
        line: returnResultLineSeries,
        lineLabel: returnResultLineLabel,
      };
    }
  }

  const cpcResultSet = formatPieChartData(chartResultSet, "cpc", 1);
  const conversionRateResultSet = formatPieChartData(
    chartResultSet,
    "conversion_rate",
    2
  );
  const costResultSet = formatPieChartData(chartResultSet, "cost", 3, 2);
  const currencySymbol = getCurrencySymbol();
  const acosResultSet = formatPieChartData(chartResultSet, "acos", 4);
  const impressionsResultSet = formatPieChartData(
    chartResultSet,
    "impressions",
    5
  );
  const clicksResultSet = formatPieChartData(chartResultSet, "clicks", 6);
  const cpmResultSet = formatPieChartData(chartResultSet, "cpm", 7);
  const ctrResultSet = formatPieChartData(chartResultSet, "ctr", 8);

  const tableTitle =
    analysisBy === "date_hour"
      ? "Cumulative Performance Trend by Date + Hour"
      : analysisBy === "date"
        ? "Cumulative Performance Trend by Date"
        : "Cumulative Weekly Performance Trend by Hour";

  const defaultOrderByData = useMemo(
    () => [[resolvedSortKey || HOUR_DIMENSION, sortConfig.direction || "asc"]],
    [resolvedSortKey, sortConfig.direction]
  );

  const ChartSkeleton = () => (
    <Skeleton
      variant="rounded"
      height={280}
      sx={{ width: "100%", borderRadius: 1 }}
    />
  );

  return (
    <div
      className={
        dashboardSideBarVisibiltyState
          ? "main-content-container-onopen-mode"
          : "main-content-container-oncolapse-mode"
      }
      id="main-box"
    >
      <div className="marketing-stream d-flex flex-column w-100 topDiv p-4">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
            bgcolor: "white",
            p: 2,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Amazon Marketing Stream
          </Typography>
          <Header
            showOnlyCalendar={true}
            includeToday={true}
            localCalendar={true}
            onLocalCalendarChange={handleLocalCalendarChange}
          />
        </Box>

        <div className="d-flex flex-column gap-8 w-100">
          <div className="campaign-list-table-data w-100 p-3 bg-white rounded">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                mb: 1,
                alignItems: "center",
              }}
            >
              <Autocomplete
                options={ANALYSIS_BY_OPTIONS}
                disableClearable
                getOptionLabel={(option) => option.label}
                value={selectedAnalysisOption}
                onChange={(_event, selectedOption) => {
                  if (selectedOption) {
                    setIsLoaderVisible(true);
                    setCurrentPage(1);
                    setSortConfig({
                      key:
                        selectedOption.value === "hour"
                          ? HOUR_DIMENSION
                          : DATE_DIMENSION,
                      direction: "asc",
                    });
                    setAnalysisBy(selectedOption.value);
                  }
                }}
                size="small"
                sx={analysisByAutocompleteSx}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Analysis by"
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputLabel-root": {
                        fontSize: "14px",
                        color: "#333333",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#333333",
                      },
                      "& .MuiInputBase-root": {
                        height: "36px",
                        fontSize: "12px !important",
                        color: "#333333",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "12px !important",
                        color: "#333333",
                        padding: "6px 8px",
                      },
                    }}
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: {
                      maxHeight: 250,
                      overflow: "auto",
                      fontSize: "12px",
                      "& .MuiAutocomplete-option": {
                        fontSize: "12px",
                        color: "#333333",
                        padding: "6px 12px",
                        "&:hover": { backgroundColor: "#EDEDED" },
                      },
                    },
                  },
                }}
              />

              <Autocomplete
                multiple
                disableCloseOnSelect
                filterOptions={(options) => options}
                options={mergedCampaignOptions}
                loading={isLoadingCampaigns}
                value={selectedCampaigns}
                inputValue={campaignInput}
                disabled={isDownloading}
                onInputChange={(_event, value) => setCampaignInput(value)}
                onChange={(_event, value) => setSelectedCampaigns(value)}
                getOptionLabel={(option) => option?.label || option?.name || ""}
                isOptionEqualToValue={(option, value) =>
                  option?.value === value?.value
                }
                renderTags={renderCampaignTags}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 0.5 }} />
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <CampaignStatusDot status={option.status} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{ fontSize: "12px", lineHeight: 1.3 }}
                          noWrap
                        >
                          {option.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "text.secondary",
                            lineHeight: 1.3,
                          }}
                          noWrap
                        >
                          ID: {option.platform_id || option.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={
                      selectedCampaigns.length ? "" : "Search campaigns"
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingCampaigns ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  flexGrow: 1,
                  minWidth: 220,
                  maxWidth: "100%",
                  "& .MuiInputBase-root": {
                    height: "36px !important",
                    minHeight: "36px !important",
                    maxHeight: "36px !important",
                    flexWrap: "nowrap !important",
                    alignItems: "center",
                    overflow: "hidden",
                    fontSize: "12px",
                    color: "#333333",
                    py: "0 !important",
                  },
                  "& .MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap !important",
                  },
                  "& .MuiAutocomplete-tag": {
                    margin: "0 2px",
                    maxWidth: "100%",
                  },
                  "& .MuiInputBase-input": {
                    fontSize: "12px",
                    color: "#333333",
                    padding: "2px 4px !important",
                    minWidth: "40px !important",
                  },
                }}
                slotProps={{
                  listbox: {
                    sx: {
                      maxHeight: 280,
                      overflow: "auto",
                      fontSize: "12px",
                    },
                  },
                }}
              />

              <Button
                variant="contained"
                size="small"
                startIcon={<SearchIcon sx={{ fontSize: "16px !important" }} />}
                onClick={handleApplyCampaignFilter}
                disabled={
                  isDownloading ||
                  isLoaderVisible ||
                  !hasPendingCampaignFilterChange
                }
                sx={{
                  textTransform: "none",
                  height: 36,
                  minWidth: 96,
                  fontSize: "12px",
                  boxShadow: "none",
                  bgcolor: "#7558ea",
                  "&:hover": { bgcolor: "#5a42c7", boxShadow: "none" },
                }}
              >
                Search
              </Button>

              <ColumnCustomizationButton
                collections={columnCollections}
                columns={metricColumns}
                activeCollectionId={columnCollectionId}
                selectedColumnKeys={selectedMetricKeys}
                onApply={handleApplyColumnCustomization}
                onSave={handleSaveColumnCustomization}
                onSaveAs={handleSaveAsColumnCustomization}
                onRenameView={handleRenameColumnView}
                onDeleteView={handleDeleteColumnView}
                saving={isColumnCustomizationSaving}
              />

              <IconButton
                onClick={handleDownload}
                disabled={isDownloading || isLoaderVisible || !dataBody.length}
                sx={{
                  border: "1px solid",
                  borderColor: "grey.400",
                  borderRadius: 1,
                  color: "#333333",
                  backgroundColor: "white",
                  "&:hover": {
                    borderColor: "grey.700",
                    backgroundColor: "grey.50",
                  },
                  width: 36,
                  height: 36,
                }}
              >
                {isDownloading ? (
                  <CircularProgress
                    size={18}
                    thickness={5}
                    sx={{ color: "grey.600" }}
                  />
                ) : (
                  <FileDownloadOutlined sx={{ fontSize: 24 }} />
                )}
              </IconButton>
            </Box>

            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: "#333333" }}
            >
              {tableTitle}
            </Typography>

            <MarketingStreamTable
              key={analysisBy}
              dataHeads={dataHeads}
              dataBody={dataBody}
              isLoading={isLoaderVisible}
              orderByData={defaultOrderByData}
              onSortClick={handleSortClick}
              summaryData={summaryData}
              groupByOptionsData={availableExtraGroupByOptions}
              onExtraGroupByChange={handleExtraGroupByChange}
              showGroupByPlusIcon={availableExtraGroupByOptions.length > 0}
              noDataFoundMessage="No marketing stream data found for the selected date range."
            />

            {(totalCount > 0 || currentPage > 1) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: "#4f5366",
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    Page Size
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 80 }}>
                    <Select
                      value={paginationLimit}
                      onChange={handlePageLimitChange}
                      disabled={isLoaderVisible}
                      sx={{
                        height: 36,
                        fontSize: "12px",
                        color: "#333333",
                      }}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          sx={{ fontSize: "12px" }}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  lazyLoading={isLoaderVisible}
                  totalItems={totalCount}
                  itemsPerPage={paginationLimit}
                />
              </Box>
            )}
          </div>

          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600, color: "#333333" }}
          >
            Performance Metrics — Hourly View
          </Typography>

          <div className="d-flex justify-content-between mt-4 mb-4 gap-4">
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">CPC</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={cpcResultSet}
                  classes={"marketing-reports"}
                  valuePrefix={currencySymbol}
                />
              )}
            </div>
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">Conversion rate</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={conversionRateResultSet}
                  classes={"marketing-reports"}
                  valueSuffix="%"
                />
              )}
            </div>
          </div>
          <div className="d-flex justify-content-between mt-4 mb-4 gap-4">
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">CPM</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={cpmResultSet}
                  classes={"marketing-reports"}
                  valuePrefix={currencySymbol}
                />
              )}
            </div>
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">CTR</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={ctrResultSet}
                  classes={"marketing-reports"}
                  valueSuffix="%"
                />
              )}
            </div>
          </div>
          <div className="d-flex justify-content-between mt-4 mb-4 gap-4">
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">Cost</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={costResultSet}
                  classes={"marketing-reports"}
                  valuePrefix={currencySymbol}
                  valueDecimals={2}
                />
              )}
            </div>
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">ACOS</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={acosResultSet}
                  classes={"marketing-reports"}
                  valueSuffix="%"
                />
              )}
            </div>
          </div>
          <div className="d-flex justify-content-between mt-4 mb-4 gap-4">
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">Impressions</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={impressionsResultSet}
                  classes={"marketing-reports"}
                />
              )}
            </div>
            <div className="w-100 min-h-300 pie-container px-2">
              <div className="px-2 pt-2">Clicks</div>
              {isLoaderVisible ? (
                <ChartSkeleton />
              ) : (
                <Chart
                  type="customBar"
                  resultSet={clicksResultSet}
                  classes={"marketing-reports"}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingStream;
